import { Account } from '@citrite/sf-api';
import { SelectedResource } from '@sharefiledev/sharing-pilet-sdk';
import {
	ConditionalLinkConditions,
	Link,
	SharablePreset,
} from '@sharefiledev/sharing-pilet-sdk/api';
import { PermissionName } from '../../constants';
import { OptionCombination } from './linkInternalTypes';
import { getPermissionKeyFromContext, isEditingAvailable } from './linkShare-utils';

export const findNextBestCombination = (
	selectedResources: SelectedResource[],
	isWaterMarkEnable: boolean,
	availableConditions: ConditionalLinkConditions[],
	availablePermissions: string[],
	conditionalLinks: Link[],
	sharablePresets: SharablePreset[],
	account: Account
): OptionCombination => {
	const availableOptionsSet = new Set<string>();
	const existingOptionsSet = new Set<string>();

	conditionalLinks.forEach(link => {
		const presentCondition = link.conditionalLinkOptions
			?.condition as ConditionalLinkConditions;
		const presentPermission = getPermissionKeyFromContext(
			link.conditionalLinkOptions?.presetRID,
			sharablePresets
		);
		existingOptionsSet.add(`${presentCondition}-${presentPermission}`);
	});

	availableConditions.forEach(availableCondition => {
		availablePermissions.forEach(availablePermission => {
			if (
				availablePermission === PermissionName.Edit &&
				(availableCondition === ConditionalLinkConditions.Anonymous ||
					availableCondition === ConditionalLinkConditions.AnonymousWithUserInfo)
			) {
				return;
			}
			availableOptionsSet.add(`${availableCondition}-${availablePermission}`);
		});
	});

	const priorityOrder: {
		condition: ConditionalLinkConditions;
		permissions: PermissionName[];
	}[] = [
		{
			condition: ConditionalLinkConditions.RequireLoginEmployeesAndClients,
			permissions: [PermissionName.Edit, PermissionName.Download, PermissionName.View],
		},
		{
			condition: ConditionalLinkConditions.RequireLoginEmployeesOnly,
			permissions: [PermissionName.Edit, PermissionName.Download, PermissionName.View],
		},
		{
			condition: ConditionalLinkConditions.AnonymousWithUserInfo,
			permissions: [PermissionName.Download, PermissionName.View],
		},
		{
			condition: ConditionalLinkConditions.Anonymous,
			permissions: [PermissionName.Download, PermissionName.View],
		},
	];

	for (const {
		condition: currentCondition,
		permissions: permissionsList,
	} of priorityOrder) {
		for (const currentPermission of permissionsList) {
			if (isWaterMarkEnable && currentPermission === PermissionName.Edit) {
				continue;
			}
			const optionKey = `${currentCondition}-${currentPermission}`;
			const isOptionPresentInExistingOptions = existingOptionsSet.has(optionKey);
			const isOptionAvailable = availableOptionsSet.has(optionKey);
			if (isOptionAvailable && !isOptionPresentInExistingOptions) {
				if (currentPermission === PermissionName.Edit) {
					if (isEditingAvailable(selectedResources, account)) {
						return { condition: currentCondition, permission: currentPermission };
					}
				} else {
					return { condition: currentCondition, permission: currentPermission };
				}
			}
		}
	}

	const [firstExistingOption] = existingOptionsSet;
	const [condition, permission] = firstExistingOption.split('-');
	return {
		condition: condition as ConditionalLinkConditions,
		permission,
	};
};
